// Vollea — Match detail with submit/confirm/dispute
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoChevronBack, IoCheckmarkCircle, IoWarning, IoAdd, IoRemove, IoTrophy } from "react-icons/io5";
import { Card, Btn, Avatar } from "../components/ui-vollea";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { C, SP, R } from "../lib/theme";

const readableError = (e) => {
  const m = e?.message;
  if (typeof m === "string" && m && m !== "[object Object]") return m;
  return "Something went wrong — please try again.";
};

const dateLabel = (iso) => {
  if (!iso) return "Date TBC";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Date TBC";
  return d.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function MatchDetail() {
  const { mid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [sets, setSets] = useState([{ team_a: 0, team_b: 0 }, { team_a: 0, team_b: 0 }]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const m = await api.get(`/matches/${mid}`);
      if (m) {
        setMatch(m);
        if (m.sets?.length) setSets(m.sets.map((s) => ({ team_a: s.team_a, team_b: s.team_b })));
      }
    } catch (e) { setMsg(readableError(e)); }
  }, [mid]);

  useEffect(() => { load(); }, [load]);

  if (!match) {
    return (
      <div style={{ padding: SP.lg, color: C.lime }}>
        <button onClick={() => navigate(-1)} style={{ color: C.text, marginBottom: SP.md }}><IoChevronBack size={26} /></button>
        Loading…
      </div>
    );
  }

  const inTeamA = match.team_a?.includes(user?.user_id);
  const inTeamB = match.team_b?.includes(user?.user_id);
  const isPlayer = inTeamA || inTeamB;
  const isOrganiser = ["club_admin", "league_organiser", "super_admin"].includes(user?.role);
  const status = match.status;
  // After a submit, the OTHER team's player is asked to confirm
  const submittedByMe = match.submitted_by === user?.user_id;
  const canConfirm = status === "pending" && (
    (match.confirm_team === "a" && inTeamA && !submittedByMe) ||
    (match.confirm_team === "b" && inTeamB && !submittedByMe) ||
    isOrganiser
  );
  const canSubmit = status === "scheduled" && (isPlayer || isOrganiser);
  const canDispute = status === "pending" && (isPlayer || isOrganiser);

  const updateSet = (idx, side, delta) => {
    setSets((prev) => prev.map((s, i) => i === idx ? { ...s, [side]: Math.max(0, Math.min(7, (s[side] || 0) + delta)) } : s));
  };
  const addSet = () => sets.length < 5 && setSets((p) => [...p, { team_a: 0, team_b: 0 }]);
  const removeSet = () => sets.length > 1 && setSets((p) => p.slice(0, -1));

  const submitResult = async () => {
    setBusy(true); setMsg("");
    try {
      const m = await api.post(`/matches/${mid}/submit`, { sets });
      setMatch(m);
      setMsg("Result submitted — waiting for the other team to confirm.");
    } catch (e) { setMsg(readableError(e)); }
    finally { setBusy(false); }
  };
  const confirmResult = async () => {
    setBusy(true); setMsg("");
    try {
      const m = await api.post(`/matches/${mid}/confirm`);
      setMatch(m);
      setMsg("Result confirmed and points applied!");
    } catch (e) { setMsg(readableError(e)); }
    finally { setBusy(false); }
  };
  const disputeResult = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/matches/${mid}/dispute`);
      setMsg("Match disputed — an organiser will review.");
      load();
    } catch (e) { setMsg(readableError(e)); }
    finally { setBusy(false); }
  };
  const overrideConfirm = async () => {
    setBusy(true); setMsg("");
    try {
      // If the organiser edited the set scores in the editor, send them as the
      // final settlement; otherwise re-send the existing recorded sets so the
      // backend's Pydantic ResultIn body always validates (it requires `sets`).
      const edited = sets.some((s, i) => s.team_a !== (match.sets?.[i]?.team_a ?? 0) || s.team_b !== (match.sets?.[i]?.team_b ?? 0));
      const body = edited ? { sets } : { sets: match.sets || sets };
      const m = await api.post(`/matches/${mid}/override-confirm`, body);
      setMatch(m);
      setMsg("Result settled by organiser. Points applied.");
    } catch (e) { setMsg(readableError(e)); }
    finally { setBusy(false); }
  };

  const winnerSide = match.winner; // "a" | "b" | null
  const setsTotal = (side) => (match.sets || []).reduce((acc, s) => acc + ((s[`team_${side}`] > s[`team_${side === "a" ? "b" : "a"}`]) ? 1 : 0), 0);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate(-1)} data-testid="match-detail-back" style={{ color: C.text, marginBottom: SP.md }}>
        <IoChevronBack size={26} />
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.md }}>
        <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{match.league_name}</span>
        <StatusChip status={status} />
      </div>
      <p style={{ color: C.textMuted, fontSize: 13, margin: "0 0 16px" }} data-testid="match-scheduled-at">{dateLabel(match.scheduled_at)} · Div {match.division || "—"}</p>

      {/* Teams */}
      <Card style={{ marginBottom: SP.lg }}>
        <TeamRow players={match.team_a_players} name={match.team_a_name} side="a" winner={winnerSide} score={status === "confirmed" ? setsTotal("a") : null} testid="match-team-a" />
        <div style={{ textAlign: "center", color: C.textDim, fontSize: 11, fontWeight: 700, margin: `${SP.sm}px 0`, letterSpacing: 1 }}>VS</div>
        <TeamRow players={match.team_b_players} name={match.team_b_name} side="b" winner={winnerSide} score={status === "confirmed" ? setsTotal("b") : null} testid="match-team-b" />
      </Card>

      {/* Submitted sets (read-only when not editable) */}
      {(status === "pending" || status === "confirmed" || status === "disputed") && match.sets?.length > 0 && (
        <Card style={{ marginBottom: SP.lg }}>
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.sm, textTransform: "uppercase" }}>Submitted sets</div>
          {match.sets.map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: `${SP.sm}px 0`, borderBottom: i < match.sets.length - 1 ? `1px solid ${C.divider}` : "none" }}>
              <span style={{ color: C.textMuted, fontSize: 13 }}>Set {i + 1}</span>
              <span style={{ color: C.text, fontWeight: 700, fontSize: 16, fontFamily: "monospace" }} data-testid={`match-set-${i}`}>{s.team_a} – {s.team_b}</span>
            </div>
          ))}
        </Card>
      )}

      {/* Submission form */}
      {canSubmit && (
        <Card style={{ marginBottom: SP.lg }}>
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.md, textTransform: "uppercase" }}>Enter set scores</div>
          {sets.map((s, idx) => (
            <SetEditor key={idx} index={idx} value={s} onChange={updateSet} />
          ))}
          <div style={{ display: "flex", gap: SP.sm, marginTop: SP.md }}>
            <button onClick={addSet} disabled={sets.length >= 5} data-testid="match-add-set" style={{ flex: 1, height: 40, borderRadius: R.md, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: sets.length >= 5 ? 0.4 : 1 }}>
              <IoAdd size={16} /> Add set
            </button>
            <button onClick={removeSet} disabled={sets.length <= 1} data-testid="match-remove-set" style={{ flex: 1, height: 40, borderRadius: R.md, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: sets.length <= 1 ? 0.4 : 1 }}>
              <IoRemove size={16} /> Remove set
            </button>
          </div>
          <div style={{ height: SP.md }} />
          <Btn label="Submit result" loading={busy} onClick={submitResult} testid="match-submit-button" />
        </Card>
      )}

      {/* Confirm / dispute actions */}
      {status === "pending" && (
        <div style={{ display: "flex", flexDirection: "column", gap: SP.sm, marginBottom: SP.lg }}>
          {canConfirm && (
            <Btn label="Confirm result" icon={<IoCheckmarkCircle size={18} />} loading={busy} onClick={confirmResult} testid="match-confirm-button" />
          )}
          {canDispute && (
            <button onClick={disputeResult} disabled={busy} data-testid="match-dispute-button" style={{ width: "100%", height: 48, borderRadius: R.md, background: "transparent", color: C.error, border: `1px solid ${C.error}`, fontWeight: 700, fontSize: 13, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: busy ? 0.5 : 1 }}>
              <IoWarning size={16} /> Dispute result
            </button>
          )}
          {submittedByMe && !isOrganiser && (
            <p style={{ color: C.textMuted, fontSize: 12, textAlign: "center", margin: 0 }}>Waiting for the other team to confirm…</p>
          )}
        </div>
      )}

      {status === "confirmed" && (
        <div data-testid="match-confirmed-banner" style={{ background: C.limeTint, border: `1px solid ${C.lime}`, padding: SP.md, borderRadius: R.md, color: C.lime, fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: SP.lg }}>
          <IoTrophy size={16} /> Result confirmed. Points applied.
        </div>
      )}

      {status === "disputed" && (
        <>
          <div data-testid="match-disputed-banner" style={{ background: "rgba(245,158,11,0.1)", border: `1px solid ${C.warning}`, padding: SP.md, borderRadius: R.md, color: C.warning, fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: SP.lg }}>
            <IoWarning size={16} /> Match disputed. An organiser will review.
          </div>
          {isOrganiser && (
            <Card style={{ marginBottom: SP.lg, border: `1px solid ${C.lime}` }}>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.sm, textTransform: "uppercase" }}>Organiser override</div>
              <p style={{ color: C.textMuted, fontSize: 13, margin: `0 0 ${SP.md}px` }}>
                Settle this dispute. Edit the set scores below if you need to correct them, then confirm to apply points.
              </p>
              {sets.map((s, idx) => (
                <SetEditor key={idx} index={idx} value={s} onChange={updateSet} />
              ))}
              <div style={{ display: "flex", gap: SP.sm, marginTop: SP.md }}>
                <button onClick={addSet} disabled={sets.length >= 5} data-testid="override-add-set" style={{ flex: 1, height: 40, borderRadius: R.md, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: sets.length >= 5 ? 0.4 : 1 }}>
                  <IoAdd size={16} /> Add set
                </button>
                <button onClick={removeSet} disabled={sets.length <= 1} data-testid="override-remove-set" style={{ flex: 1, height: 40, borderRadius: R.md, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: sets.length <= 1 ? 0.4 : 1 }}>
                  <IoRemove size={16} /> Remove set
                </button>
              </div>
              <div style={{ height: SP.md }} />
              <Btn label="Override & confirm" icon={<IoCheckmarkCircle size={18} />} loading={busy} onClick={overrideConfirm} testid="match-override-confirm-button" />
            </Card>
          )}
        </>
      )}

      {msg ? <p data-testid="match-msg" style={{ color: msg.includes("submitted") || msg.includes("confirmed") || msg.includes("settled") || msg.includes("applied") ? C.lime : C.warning, fontSize: 13 }}>{msg}</p> : null}
    </div>
  );
}

function TeamRow({ players, name, side, winner, score, testid }) {
  const isWinner = winner === side;
  return (
    <div data-testid={testid} style={{ display: "flex", alignItems: "center", gap: SP.md }}>
      <div style={{ display: "flex", marginLeft: -8 }}>
        {(players || []).map((p, i) => (
          <div key={i} style={{ marginLeft: i === 0 ? 8 : -10, position: "relative", zIndex: players.length - i }}>
            <Avatar uri={p.photo} name={p.name} size={36} style={{ border: `2px solid ${C.surface}` }} />
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: isWinner ? C.lime : C.text, fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
          {(players || []).map((p) => p.name).join(" & ") || "TBC"}
          {isWinner && <IoTrophy size={14} color={C.lime} />}
        </div>
        {name && <div style={{ color: C.textMuted, fontSize: 12 }}>{name}</div>}
      </div>
      {score !== null && score !== undefined && (
        <div style={{ color: isWinner ? C.lime : C.text, fontWeight: 700, fontSize: 24 }}>{score}</div>
      )}
    </div>
  );
}

function SetEditor({ index, value, onChange }) {
  const Cell = ({ side, val }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button onClick={() => onChange(index, side, -1)} data-testid={`set-${index}-${side}-minus`} style={{ width: 30, height: 30, borderRadius: R.sm, background: C.surface3, color: C.text }}>−</button>
      <span style={{ color: C.text, fontWeight: 700, fontSize: 22, fontFamily: "monospace", width: 32, textAlign: "center" }} data-testid={`set-${index}-${side}-value`}>{val}</span>
      <button onClick={() => onChange(index, side, +1)} data-testid={`set-${index}-${side}-plus`} style={{ width: 30, height: 30, borderRadius: R.sm, background: C.surface3, color: C.text }}>+</button>
    </div>
  );
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${SP.sm}px 0`, borderBottom: `1px solid ${C.divider}` }}>
      <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 600 }}>SET {index + 1}</span>
      <Cell side="team_a" val={value.team_a} />
      <span style={{ color: C.textDim, fontWeight: 700 }}>:</span>
      <Cell side="team_b" val={value.team_b} />
    </div>
  );
}

function StatusChip({ status }) {
  const conf = {
    scheduled: { bg: C.surface3, fg: C.text, text: "Scheduled" },
    pending: { bg: "rgba(245,158,11,0.15)", fg: C.warning, text: "Pending" },
    confirmed: { bg: C.limeTint, fg: C.lime, text: "Confirmed" },
    disputed: { bg: "rgba(239,68,68,0.15)", fg: C.error, text: "Disputed" },
  }[status] || { bg: C.surface3, fg: C.textMuted, text: status };
  return (
    <span data-testid={`match-status-${status}`} style={{ background: conf.bg, color: conf.fg, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
      {conf.text}
    </span>
  );
}
